/**
 * Cention Upload Adapter for CKEditor 5
 * 
 * This plugin replaces the Base64UploadAdapter to upload images to the server
 * immediately when pasted or dropped, similar to the old CKEditor 4 behavior.
 * This is essential for social media platforms that don't support base64 inline images.
 */

import { Plugin } from 'ckeditor5';

export default class CentionUploadAdapter extends Plugin {
	static get pluginName() {
		return 'CentionUploadAdapter';
	}

	static get requires() {
		return ['FileRepository'];
	}

	init() {
		const options = this.editor.config.get('centionUpload') || {};

		if (options.disabled === true) {
			return;
		}

		this.editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
			return new Adapter(loader, options);
		};
	}
}

class Adapter {
	constructor(loader, options) {
		this.loader = loader;
		this.options = options;
		this.xhr = null;
	}

	upload() {
		return this.loader.file.then(
			(file) =>
				new Promise((resolve, reject) => {
					this._initRequest();
					this._initListeners(resolve, reject, file);
					this._sendRequest(file);
				})
		);
	}

	abort() {
		if (this.xhr) {
			this.xhr.abort();
		}
	}

	_initRequest() {
		const xhr = (this.xhr = new XMLHttpRequest());

		// Get the upload URL from options or use default
		const uploadUrl = this.options.uploadUrl || (window.webserverRoot + 'errand/uploadAnswerAttachment');
		
		xhr.open('POST', uploadUrl, true);
		xhr.responseType = 'json';
	}

	_initListeners(resolve, reject, file) {
		const xhr = this.xhr;
		const loader = this.loader;
		const genericErrorText = `Couldn't upload file: ${file.name}.`;

		xhr.addEventListener('error', () => reject(genericErrorText));
		xhr.addEventListener('abort', () => reject());
		xhr.addEventListener('load', () => {
			const response = xhr.response;

			if (!response || response.error) {
				console.error('CKEditor5: Image upload failed', response?.error);
				return reject(response && response.error ? response.error.message : genericErrorText);
			}

			// The response should contain the image URL
			// Expected response format: { download: 'url', id: 'id', value: 'filename' }
			let imageUrl = response.download;
			
			// Ensure URL is absolute
			if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
				imageUrl = '/' + imageUrl;
			}

			console.log('CKEditor5: Image uploaded successfully', {
				id: response.id,
				url: imageUrl,
				filename: response.value || file.name
			});

			// Store the uploaded image in a global tracker for social media platforms
			// This allows us to track uploaded images as separate attachments
			if (typeof window.CKEditor5UploadedImages === 'undefined') {
				window.CKEditor5UploadedImages = [];
			}
			window.CKEditor5UploadedImages.push({
				id: response.id,
				download: response.download,
				value: response.value || file.name,
				url: imageUrl
			});

			// Call the onUpload callback if provided
			if (this.options.onUpload && typeof this.options.onUpload === 'function') {
				this.options.onUpload(response, file);
			}

			resolve({
				default: imageUrl,
				// Store the attachment ID for later use
				attachmentId: response.id
			});
		});

		if (xhr.upload) {
			xhr.upload.addEventListener('progress', (evt) => {
				if (evt.lengthComputable) {
					loader.uploadTotal = evt.total;
					loader.uploaded = evt.loaded;
				}
			});
		}
	}

	_sendRequest(file) {
		const data = new FormData();

		data.append('uploadfile', file);
		data.append('fileNameOnly', file.name || 'inline-image.png');
		
		// Determine event type based on file name pattern
		let eventType = 'paste';
		if (file.name && !file.name.match(/^image\.(png|jpg|jpeg|gif)$/i)) {
			eventType = 'drag';
		}
		data.append('eventType', eventType);
		data.append('fileType', file.type);

		const boundary = Math.floor(Math.random() * 6) + '' + Date.now();
		data.append('random', boundary);

		// Log the upload for debugging
		console.log('CKEditor5: Uploading image to server', {
			fileName: file.name,
			fileType: file.type,
			eventType: eventType,
			uploadUrl: this.options.uploadUrl || (window.webserverRoot + 'errand/uploadAnswerAttachment')
		});

		// CRITICAL: This sends the image to the server via POST request
		// This is called for ALL pasted, dragged, or uploaded images when CentionUploadAdapter is active
		this.xhr.send(data);
	}
}

